// DER (Daily Energy Requirement) Multipliers Table
// Data source: backend/data/der_table.json

interface ActivityMultipliers {
  "Low Activity": number;
  "Moderate Activity": number;
  "High Activity": number;
}

interface AgeMultipliers {
  "1-3 months": number;
  "4-6 months": number;
  "7-9 months": number;
  "10-12 months": number;
  "13-15 months": ActivityMultipliers;
  "Adult": ActivityMultipliers;
  "Overweight (BCS>5)": number;
}

interface SizeMultipliers {
  [key: string]: AgeMultipliers;
}

interface DERTable {
  DogFeedingMultipliers: {
    Intact: SizeMultipliers;
    "Neutered/Spayed": SizeMultipliers;
  };
}

const DER_TABLE: DERTable = {
  "DogFeedingMultipliers": {
    "Intact": {
      "Toy-Small": {
        "1-3 months": 3,
        "4-6 months": 2.5,
        "7-9 months": 2,
        "10-12 months": 1.8,
        "13-15 months": {
          "Low Activity": 1.4,
          "Moderate Activity": 1.6,
          "High Activity": 2
        },
        "Adult": {
          "Low Activity": 1.4,
          "Moderate Activity": 1.6,
          "High Activity": 1.8
        },
        "Overweight (BCS>5)": 1
      },
      "Small": {
        "1-3 months": 3,
        "4-6 months": 2.5,
        "7-9 months": 2,
        "10-12 months": 1.8,
        "13-15 months": {
          "Low Activity": 1.4,
          "Moderate Activity": 1.6,
          "High Activity": 2
        },
        "Adult": {
          "Low Activity": 1.4,
          "Moderate Activity": 1.6,
          "High Activity": 1.8
        },
        "Overweight (BCS>5)": 1
      },
      "Small-Medium": {
        "1-3 months": 3,
        "4-6 months": 2.5,
        "7-9 months": 1.8,
        "10-12 months": 1.8,
        "13-15 months": {
          "Low Activity": 1.4,
          "Moderate Activity": 1.6,
          "High Activity": 2
        },
        "Adult": {
          "Low Activity": 1.4,
          "Moderate Activity": 1.6,
          "High Activity": 1.8
        },
        "Overweight (BCS>5)": 1
      },
      "Medium (25-30)": {
        "1-3 months": 3,
        "4-6 months": 2.5,
        "7-9 months": 2,
        "10-12 months": 1.8,
        "13-15 months": {
          "Low Activity": 1.4,
          "Moderate Activity": 1.6,
          "High Activity": 2
        },
        "Adult": {
          "Low Activity": 1.4,
          "Moderate Activity": 1.6,
          "High Activity": 1.8
        },
        "Overweight (BCS>5)": 1
      },
      "Medium (30-40)": {
        "1-3 months": 3,
        "4-6 months": 2.5,
        "7-9 months": 2,
        "10-12 months": 1.8,
        "13-15 months": {
          "Low Activity": 1.4,
          "Moderate Activity": 1.6,
          "High Activity": 2
        },
        "Adult": {
          "Low Activity": 1.4,
          "Moderate Activity": 1.6,
          "High Activity": 1.8
        },
        "Overweight (BCS>5)": 1
      },
      "Medium (40-50)": {
        "1-3 months": 3,
        "4-6 months": 2.5,
        "7-9 months": 2,
        "10-12 months": 1.8,
        "13-15 months": {
          "Low Activity": 1.4,
          "Moderate Activity": 1.6,
          "High Activity": 2
        },
        "Adult": {
          "Low Activity": 1.4,
          "Moderate Activity": 1.6,
          "High Activity": 1.8
        },
        "Overweight (BCS>5)": 1
      },
      "Medium-Large": {
        "1-3 months": 3,
        "4-6 months": 2.5,
        "7-9 months": 2,
        "10-12 months": 1.8,
        "13-15 months": {
          "Low Activity": 1.4,
          "Moderate Activity": 1.6,
          "High Activity": 2
        },
        "Adult": {
          "Low Activity": 1.4,
          "Moderate Activity": 1.6,
          "High Activity": 1.8
        },
        "Overweight (BCS>5)": 1
      },
      "Large": {
        "1-3 months": 3,
        "4-6 months": 2.5,
        "7-9 months": 2,
        "10-12 months": 2,
        "13-15 months": {
          "Low Activity": 1.4,
          "Moderate Activity": 1.6,
          "High Activity": 1.8
        },
        "Adult": {
          "Low Activity": 1.4,
          "Moderate Activity": 1.6,
          "High Activity": 1.8
        },
        "Overweight (BCS>5)": 1
      },
      "Large-Giant": {
        "1-3 months": 3,
        "4-6 months": 2.5,
        "7-9 months": 2.5,
        "10-12 months": 2,
        "13-15 months": {
          "Low Activity": 1.4,
          "Moderate Activity": 1.8,
          "High Activity": 2
        },
        "Adult": {
          "Low Activity": 1.4,
          "Moderate Activity": 1.6,
          "High Activity": 1.8
        },
        "Overweight (BCS>5)": 1
      }
    },
    "Neutered/Spayed": {
      "Toy-Small": {
        "1-3 months": 3,
        "4-6 months": 2.5,
        "7-9 months": 1.8,
        "10-12 months": 1.6,
        "13-15 months": {
          "Low Activity": 1.2,
          "Moderate Activity": 1.4,
          "High Activity": 1.8
        },
        "Adult": {
          "Low Activity": 1.2,
          "Moderate Activity": 1.4,
          "High Activity": 1.6
        },
        "Overweight (BCS>5)": 1
      },
      "Small": {
        "1-3 months": 3,
        "4-6 months": 2.5,
        "7-9 months": 2,
        "10-12 months": 1.8,
        "13-15 months": {
          "Low Activity": 1.2,
          "Moderate Activity": 1.4,
          "High Activity": 1.8
        },
        "Adult": {
          "Low Activity": 1.2,
          "Moderate Activity": 1.4,
          "High Activity": 1.6
        },
        "Overweight (BCS>5)": 1
      },
      "Small-Medium": {
        "1-3 months": 3,
        "4-6 months": 2.5,
        "7-9 months": 2,
        "10-12 months": 1.8,
        "13-15 months": {
          "Low Activity": 1.2,
          "Moderate Activity": 1.4,
          "High Activity": 1.8
        },
        "Adult": {
          "Low Activity": 1.2,
          "Moderate Activity": 1.4,
          "High Activity": 1.6
        },
        "Overweight (BCS>5)": 1
      },
      "Medium (25-30)": {
        "1-3 months": 3,
        "4-6 months": 2.5,
        "7-9 months": 2,
        "10-12 months": 1.8,
        "13-15 months": {
          "Low Activity": 1.2,
          "Moderate Activity": 1.4,
          "High Activity": 1.8
        },
        "Adult": {
          "Low Activity": 1.2,
          "Moderate Activity": 1.4,
          "High Activity": 1.6
        },
        "Overweight (BCS>5)": 1
      },
      "Medium (30-40)": {
        "1-3 months": 3,
        "4-6 months": 2.5,
        "7-9 months": 2,
        "10-12 months": 1.8,
        "13-15 months": {
          "Low Activity": 1.2,
          "Moderate Activity": 1.4,
          "High Activity": 1.8
        },
        "Adult": {
          "Low Activity": 1.2,
          "Moderate Activity": 1.4,
          "High Activity": 1.6
        },
        "Overweight (BCS>5)": 1
      },
      "Medium (40-50)": {
        "1-3 months": 3,
        "4-6 months": 2.5,
        "7-9 months": 2,
        "10-12 months": 1.8,
        "13-15 months": {
          "Low Activity": 1.2,
          "Moderate Activity": 1.4,
          "High Activity": 1.8
        },
        "Adult": {
          "Low Activity": 1.2,
          "Moderate Activity": 1.4,
          "High Activity": 1.6
        },
        "Overweight (BCS>5)": 1
      },
      "Medium-Large": {
        "1-3 months": 3,
        "4-6 months": 2.5,
        "7-9 months": 2,
        "10-12 months": 1.8,
        "13-15 months": {
          "Low Activity": 1.2,
          "Moderate Activity": 1.4,
          "High Activity": 1.8
        },
        "Adult": {
          "Low Activity": 1.2,
          "Moderate Activity": 1.4,
          "High Activity": 1.6
        },
        "Overweight (BCS>5)": 1
      },
      "Large": {
        "1-3 months": 3,
        "4-6 months": 2.5,
        "7-9 months": 2,
        "10-12 months": 2,
        "13-15 months": {
          "Low Activity": 1.2,
          "Moderate Activity": 1.4,
          "High Activity": 1.6
        },
        "Adult": {
          "Low Activity": 1.2,
          "Moderate Activity": 1.4,
          "High Activity": 1.6
        },
        "Overweight (BCS>5)": 1
      },
      "Large-Giant": {
        "1-3 months": 3,
        "4-6 months": 3,
        "7-9 months": 2.5,
        "10-12 months": 2,
        "13-15 months": {
          "Low Activity": 1.2,
          "Moderate Activity": 1.6,
          "High Activity": 1.8
        },
        "Adult": {
          "Low Activity": 1.2,
          "Moderate Activity": 1.4,
          "High Activity": 1.6
        },
        "Overweight (BCS>5)": 1
      }
    }
  }
};

/**
 * Map breed size category to DER table key
 * For "Medium" breeds, uses weight to determine subcategory
 */
export const mapSizeCategory = (sizeCategory: string, weightKg: number): string => {
  // If it's a Medium breed, determine the subcategory based on weight
  if (sizeCategory === "Medium") {
    if (weightKg >= 25 && weightKg < 30) {
      return "Medium (25-30)";
    } else if (weightKg >= 30 && weightKg < 40) {
      return "Medium (30-40)";
    } else if (weightKg >= 40 && weightKg <= 50) {
      return "Medium (40-50)";
    } else if (weightKg < 25) {
      return "Medium (25-30)";
    } else {
      return "Medium (40-50)";
    }
  }
  
  // For Variable (Mixed Breed), use weight-based size estimation
  if (sizeCategory === "Variable") {
    if (weightKg < 10) return "Small";
    if (weightKg < 15) return "Small-Medium";
    if (weightKg < 25) return "Medium (25-30)";
    if (weightKg < 30) return "Medium (25-30)";
    if (weightKg < 40) return "Medium (30-40)";
    if (weightKg < 50) return "Medium (40-50)";
    if (weightKg < 65) return "Large";
    return "Large-Giant";
  }
  
  return sizeCategory;
};

/**
 * Get DER multiplier from the table
 * For BCS > 5 (overweight), always returns 1 per veterinary guidelines
 * This means overweight dogs get RER only (maintenance for weight loss)
 */
export const getDERMultiplier = (
  neuteredStatus: 'neutered' | 'intact',
  sizeCategory: string,
  age: string,
  activity: 'low' | 'normal' | 'high',
  bcs: number
): number => {
  // For overweight dogs (BCS > 5), multiplier is always 1
  // This follows the DER table's "Overweight (BCS>5)": 1 guideline
  if (bcs > 5) {
    return 1;
  }

  const neuteredKey = neuteredStatus === 'neutered' ? 'Neutered/Spayed' : 'Intact';
  const sizeData = DER_TABLE.DogFeedingMultipliers[neuteredKey][sizeCategory];
  
  if (!sizeData) {
    console.error(`Size category "${sizeCategory}" not found in DER table`);
    return 1.4; // Default fallback (moderate activity neutered)
  }

  // Map age to DER table format
  const ageMap: { [key: string]: string } = {
    '1-3months': '1-3 months',
    '4-6months': '4-6 months',
    '7-9months': '7-9 months',
    '10-12months': '10-12 months',
    '13-15months': '13-15 months',
    '16-18months': 'Adult', // Map to Adult for 16-18 months
    '19-24months': 'Adult', // Map to Adult for 19-24 months
    'adult': 'Adult'
  };
  
  const ageKey = ageMap[age] as keyof AgeMultipliers;
  const ageData = sizeData[ageKey];

  let baseMultiplier: number;

  // For ages that require activity level
  if (typeof ageData === 'object') {
    const activityMap: { [key: string]: keyof ActivityMultipliers } = {
      'low': 'Low Activity',
      'normal': 'Moderate Activity',
      'high': 'High Activity'
    };
    
    baseMultiplier = ageData[activityMap[activity]];
  } else {
    // For ages that don't require activity level (young puppies)
    baseMultiplier = ageData;
  }
  
  return baseMultiplier;
};

/**
 * Calculate ideal body weight based on BCS (Body Condition Score)
 * Uses veterinary-standard percentages for each BCS level
 * 
 * Per PawCo guidelines:
 * - BCS 4 or 5: Use current weight (no adjustment)
 * - BCS 1-3: Underweight - calculate target ideal weight
 * - BCS 6-9: Overweight - calculate target ideal weight
 */
export const calculateIdealWeight = (currentWeight: number, bcs: number): number => {
  // BCS 4 or 5 - use current weight (no adjustment)
  // Per feedback: "If BCS is 4 or 5, do not adjust body weight"
  if (bcs === 4 || bcs === 5) {
    return currentWeight;
  }
  
  // For underweight dogs (BCS 1-3), current weight is below ideal
  // Using corrected percentages based on veterinary standards:
  // BCS 3: ~8% under ideal weight -> ideal = current / 0.92
  // BCS 2: ~17% under ideal weight -> ideal = current / 0.83
  // BCS 1: ~25% under ideal weight -> ideal = current / 0.75
  if (bcs < 4) {
    const pointsUnder = 4 - bcs; // 1, 2, or 3 points below BCS 4
    // Use approximately 8.5% per BCS point, capped at 25%
    const percentUnder = Math.min(pointsUnder * 0.085, 0.25);
    return currentWeight / (1 - percentUnder);
  }
  
  // For overweight dogs (BCS 6-9), current weight is above ideal
  // BCS 6: 10% over ideal weight -> ideal = current / 1.10
  // BCS 7: 20% over ideal weight -> ideal = current / 1.20  
  // BCS 8: 30% over ideal weight -> ideal = current / 1.30
  // BCS 9: 40% over ideal weight -> ideal = current / 1.40
  const percentOver = (bcs - 5) * 0.10;
  return currentWeight / (1 + percentOver);
};

/**
 * Calculate Resting Energy Requirement (RER)
 */
export const calculateRER = (weightKg: number): number => {
  // RER = 70 x (body weight in kg)^0.75
  return 70 * Math.pow(weightKg, 0.75);
};

